import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../services/firebase';
import {
  getCurrentUserRoleForChild, 
  hasCurrentUserPermission,
  getUserDisplayInfo,
  getChildrenWithRoles,
  populateChildTeamMembers
} from '../services/rolePermissionService';
import {
  USER_ROLES,
  PERMISSIONS,
  canInviteAnyone as roleCanInviteAnyone,
  canManageTeam as roleCanManageTeam,
} from '../constants/roles';
import { getE2EMockRoleContext, isE2EMockEnabled } from '../services/e2eMock';
import { persistMissingChildProfileColors } from '../services/childProfileColorService';

const defaultRoleContext = {
  userRoles: {},
  userPermissions: {},
  userDisplayInfo: {},
  childrenWithAccess: [],
  loading: true,
  getUserRoleForChild: () => null,
  hasPermissionForChild: () => false,
  canAddDataForChild: () => true,
  isReadOnlyForChild: () => false,
  getDisplayInfoForChild: () => null,
  isPrimaryCaregiver: () => false,
  canInviteOthers: () => false,
  canManageChild: () => false,
  refreshRoles: async () => {},
  USER_ROLES,
  PERMISSIONS,
};

const RoleContext = createContext(defaultRoleContext);

const normalizeChildPhoto = (child) => ({
  ...child,
  profilePhoto: child?.profilePhoto || child?.photoURL || child?.avatarUrl || null,
});

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

export const RoleProvider = ({ children }) => {
  const [user] = useAuthState(auth);
  const [userRoles, setUserRoles] = useState({}); // childId -> role mapping
  const [userPermissions, setUserPermissions] = useState({}); // childId -> permissions array
  const [userDisplayInfo, setUserDisplayInfo] = useState({}); // childId -> display info
  const [childrenWithAccess, setChildrenWithAccess] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMockMode = isE2EMockEnabled();

  // Load user's roles and permissions for all children
  useEffect(() => {
    if (isMockMode) {
      return;
    }

    const loadUserRolesAndPermissions = async () => {
      if (!user) {
        setUserRoles({});
        setUserPermissions({});
        setUserDisplayInfo({});
        setChildrenWithAccess([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Get all children with roles
        const childrenWithRoles = await getChildrenWithRoles();
        
        // Populate team member details for each child
        const childrenWithTeamDetails = await Promise.all(
          childrenWithRoles.map(populateChildTeamMembers)
        );
        
        const childrenWithProfileColors = await persistMissingChildProfileColors(childrenWithTeamDetails, user.uid);
        setChildrenWithAccess(childrenWithProfileColors.map(normalizeChildPhoto));

        // Build role, permission, and display info mappings
        const roleMap = {};
        const permissionMap = {};
        const displayInfoMap = {};

        for (const child of childrenWithTeamDetails) {
          roleMap[child.id] = child.userRole;
          permissionMap[child.id] = child.permissions || [];
          
          // Get display info
          const displayInfo = await getUserDisplayInfo(user.uid, child.id);
          displayInfoMap[child.id] = displayInfo;
        }

        setUserRoles(roleMap);
        setUserPermissions(permissionMap);
        setUserDisplayInfo(displayInfoMap);
      } catch (error) {
        console.error('Error loading user roles:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserRolesAndPermissions();
  }, [user, isMockMode]);

  // Helper functions
  const getUserRoleForChild = (childId) => {
    return userRoles[childId] || null;
  };

  const hasPermissionForChild = (childId, permission) => {
    const permissions = userPermissions[childId] || [];
    return permissions.includes(permission);
  };

  const canAddDataForChild = (childId) => {
    return hasPermissionForChild(childId, PERMISSIONS.ADD_DAILY_LOGS);
  };

  const isReadOnlyForChild = (childId) => {
    return getUserRoleForChild(childId) === USER_ROLES.THERAPIST;
  };

  const getDisplayInfoForChild = (childId) => {
    return userDisplayInfo[childId] || null;
  };

  const isPrimaryCaregiver = (childId) => {
    return getUserRoleForChild(childId) === USER_ROLES.CARE_OWNER;
  };

  const canInviteOthers = (childId) => {
    const role = getUserRoleForChild(childId);
    return roleCanInviteAnyone(role);
  };

  const canManageChild = (childId) => {
    const role = getUserRoleForChild(childId);
    return roleCanManageTeam(role);
  };

  // Refresh roles (useful after inviting new members)
  const refreshRoles = async () => {
    if (!user) return;
    
    try {
      const childrenWithRoles = await getChildrenWithRoles();
      const childrenWithTeamDetails = await Promise.all(
        childrenWithRoles.map(populateChildTeamMembers)
      );
      const childrenWithProfileColors = await persistMissingChildProfileColors(childrenWithTeamDetails, user.uid);
      setChildrenWithAccess(childrenWithProfileColors.map(normalizeChildPhoto));

      const roleMap = {};
      const permissionMap = {};
      const displayInfoMap = {};

      for (const child of childrenWithTeamDetails) {
        roleMap[child.id] = child.userRole;
        permissionMap[child.id] = child.permissions || [];
        
        const displayInfo = await getUserDisplayInfo(user.uid, child.id);
        displayInfoMap[child.id] = displayInfo;
      }

      setUserRoles(roleMap);
      setUserPermissions(permissionMap);
      setUserDisplayInfo(displayInfoMap);
    } catch (error) {
      console.error('Error refreshing roles:', error);
    }
  };

  const value = {
    // State
    userRoles,
    userPermissions,
    userDisplayInfo,
    childrenWithAccess,
    loading,

    // Helper functions
    getUserRoleForChild,
    hasPermissionForChild,
    canAddDataForChild,
    isReadOnlyForChild,
    getDisplayInfoForChild,
    isPrimaryCaregiver,
    canInviteOthers,
    canManageChild,
    refreshRoles,

    // Constants for easy access
    USER_ROLES,
    PERMISSIONS
  };

  if (isMockMode) {
    const mockContext = getE2EMockRoleContext();

    return (
      <RoleContext.Provider value={{
        ...defaultRoleContext,
        ...mockContext,
        getUserRoleForChild: (childId) => mockContext.userRoles[childId] || null,
        hasPermissionForChild: () => true,
        canAddDataForChild: () => true,
        isReadOnlyForChild: () => false,
        getDisplayInfoForChild: (childId) => mockContext.userDisplayInfo[childId] || null,
        isPrimaryCaregiver: () => true,
        canInviteOthers: () => true,
        canManageChild: () => true,
        refreshRoles: async () => {},
      }}>
        {children}
      </RoleContext.Provider>
    );
  }

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export default RoleContext;
